import { configureApi } from '@api/api'
import { queryClient } from '@api/query-client'
import { activateChunkRecovery } from '@core/utils/error-handling/activate-chunk-recovery'
import { initSentry } from '@core/utils/error-handling/init-sentry'
import { rootErrorHandlers } from '@core/utils/error-handling/root-error-handlers'
import { ThemeProvider } from '@design/contexts/ThemeContext'
import { TooltipProvider } from '@design/ui/tooltip'
import { QueryClientProvider } from '@tanstack/react-query'
import { type AnyRouter, RouterProvider } from '@tanstack/react-router'
import type { i18n as I18nInstance } from 'i18next'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { I18nextProvider } from 'react-i18next'

type StartAppOptions = {
  router: AnyRouter
  i18n: I18nInstance
  i18nInit: Promise<unknown>
  sentryDsn: string | undefined
  logOutPath: string
  themeStorageKey: string
}

export function startApp({
  router,
  i18n,
  i18nInit,
  sentryDsn,
  logOutPath,
  themeStorageKey,
}: StartAppOptions) {
  initSentry(sentryDsn, router)
  activateChunkRecovery(router)

  i18nInit.then(() => {
    configureApi({
      onUnauthorized: () =>
        router.navigate({
          to: logOutPath,
          search: { redirect: router.state.location.href },
        }),
      language: i18n.language,
    })

    const rootElement = document.getElementById('root')
    if (!rootElement) throw new Error('Root element #root not found')

    const root = ReactDOM.createRoot(rootElement, rootErrorHandlers)
    root.render(
      <StrictMode>
        <I18nextProvider i18n={i18n}>
          <TooltipProvider>
            <ThemeProvider defaultTheme='system' storageKey={themeStorageKey}>
              <QueryClientProvider client={queryClient}>
                <RouterProvider router={router} />
              </QueryClientProvider>
            </ThemeProvider>
          </TooltipProvider>
        </I18nextProvider>
      </StrictMode>
    )
  })
}
